import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonIcon,
  IonTextarea,
  IonButton,
} from '@ionic/react';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import DynamicButton from 'components/buttons/DynamicButton';
import { personCircleOutline, chatbubbleEllipsesOutline } from 'ionicons/icons';
import { askAgent } from 'services/freak_agent';
import './AgentModal.css';

export interface AgentModalProps extends React.ComponentProps<typeof IonModal> {
  isOpen: boolean;
  onDidDismiss: () => void;
  agentName?: string;
}

const AgentModal: React.FC<AgentModalProps> = ({
  isOpen,
  onDidDismiss,
  agentName = 'AI Copilot',
  ...rest
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const [messages, setMessages] = useState([
    { sender: 'agent', text: t('Hallo! Wie kann ich dir helfen?') as string },
  ]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleClose = () => {
    onDidDismiss?.();
    modalRef.current?.dismiss();
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    setIsSending(true);
    setMessages((msgs) => [...msgs, { sender: 'user', text: input }]);
    try {
      const agentResponse = await askAgent(input);
      setMessages((msgs) => [
        ...msgs,
        { sender: 'agent', text: agentResponse },
      ]);
    } catch (err) {
      setMessages((msgs) => [
        ...msgs,
        { sender: 'agent', text: 'Fehler beim Abrufen der Antwort.' },
      ]);
    }
    setIsSending(false);
    setInput('');
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="edit-modal"
      {...rest}
    >
      <IonHeader className="ion-no-border">
        <IonToolbar className="edit-modal-toptoolbar">
          <IonTitle>
            <Trans i18nKey="AGENT_CHAT">{agentName} Chat</Trans>
          </IonTitle>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={handleClose}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="agent-modal-wrapper">
          <div className="agent-chat-history">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`agent-chat-msg ${
                  msg.sender === 'agent' ? 'agent' : 'user'
                }`}
              >
                <IonIcon
                  icon={
                    msg.sender === 'agent'
                      ? chatbubbleEllipsesOutline
                      : personCircleOutline
                  }
                  className="agent-chat-icon"
                />
                <span>{msg.text}</span>
              </div>
            ))}
          </div>
          <div className="agent-chat-input-row">
            <IonTextarea
              value={input}
              onIonInput={(e) => {
                // Ionic gibt e.detail.value für IonTextarea
                // Typ: CustomEvent<{ value: string }>
                const value = (e as CustomEvent).detail?.value ?? '';
                setInput(value);
              }}
              placeholder={t('Nachricht eingeben...') as string}
              rows={1}
              style={{ borderRadius: '8px' }}
              disabled={isSending}
            />
            <IonButton
              onClick={handleSend}
              disabled={isSending || !input.trim()}
              color="primary"
              className="agent-chat-send-btn"
            >
              <Trans i18nKey="SEND">Senden</Trans>
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default AgentModal;
